/**
 * EditDeviceEnum Component
 * 
 * Dialog component for editing existing device enum records.
 * Similar to AddDeviceEnum but pre-populates form with existing data.
 * 
 * Features:
 * - Loads existing device data into form
 * - Allows updating all device properties
 * - Supports image updates (replaces existing images)
 * - Manages sensors, I/O, and hardware lists
 * - Validates input data
 * - Updates Firestore and  Storage
 * - Provides user feedback via snackbars
 * 
 * @param {boolean} isDialogOpen - Controls dialog visibility
 * @param {Function} handleClose - Function to close the dialog
 * @param {Object} deviceEnumInitData - Existing device data to edit
 */
import React, { useEffect, useState } from "react";

import { doc, updateDoc } from "firebase/firestore";
import { useDropzone } from "react-dropzone";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import { Switch, Snackbar } from "@mui/material";
import Resizer from "react-image-file-resizer";

import {
  ref,
  getDownloadURL,
  deleteObject,
  uploadBytes,
} from "firebase/storage";

import { ImageLoader } from "../../../../components/ui/img/ImageLoader";
import { firebaseStorage } from "../../../../components/firebase/storage";
import { firebaseStore } from "../../../../components/firebase/firestore";
import { primaryColorTextField } from "../../../../components/theme/globalStyles";
import {
  deviceSensorEnums,
  deviceIoEnums,
} from "../../../../components/utils/enums";

import "./DeviceEnumsComp.css";

function EditDeviceEnum({
  isDialogOpen,
  handleClose,
  deviceEnumInitData,
}: any) {
  /**
   * Dropzone for icon image upload
   */
  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/jpeg": [],
      "image/png": [],
    },
    maxFiles: 1,
    onDrop: (files) => onIconDrop(files),
  });
  
  /**
   * Dropzone for image gallery (max 5 images)
   */
  const {
    getRootProps: getRootPropsImageArray,
    getInputProps: getInputPropsImageArray,
  } = useDropzone({
    accept: {
      "image/jpeg": [],
      "image/png": [],
    },
    maxFiles: 5,
    onDrop: (files) => onImageDropArray(files),
  });
  
  // UI state
  const [dialogLoading, setDialogLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // Basic device information state
  const [deviceName, setDeviceName] = useState<string>("");
  const [deviceType, setDeviceType] = useState<string>("");
  const [deviceSubtitle, setDeviceSubtitle] = useState<string>("");
  const [deviceDescription, setDeviceDescription] = useState<string>("");
  const [deviceOS, setDeviceOS] = useState<string>("");
  
  // Image state
  const [iconPath, setIconPath] = useState<any>(null);
  const [imagePathArray, setImagePathArray] = useState<any[]>([]);
  
  // Sensors state
  const [sensorList, setSensorList] = useState<any[]>([]);
  const [sensorActualName, setSensorActualName] = useState<string>("");
  const [sensorActualType, setSensorActualType] = useState<string>("");
  const [sensorActualUnit, setSensorActualUnit] = useState<string>("");
  const [sensorActualHardware, setSensorActualHardware] = useState<string>("");
  
  // I/O state
  const [ioList, setIoList] = useState<any[]>([]);
  const [ioActualName, setIoActualName] = useState<string>("");
  const [ioActualType, setIoActualType] = useState<string>("");
  const [ioActualHardware, setIoActualHardware] = useState<string>("");
  
  // Hardware components
  const [hardwareList, setHardwareList] = useState<string[]>([]);
  const [hardwareActual, setHardwareActual] = useState<string>("");
  
  // External links
  const [githubLink, setGithubLink] = useState<string>("");
  const [webLink, setWebLink] = useState<string>("");
  
  // Status flags
  const [isPrivate, setIsPrivate] = useState<boolean>(false);
  const [isAlpha, setIsAlpha] = useState<boolean>(false);
  const [isBeta, setIsBeta] = useState<boolean>(false);

  /**
   * Initialize form with existing device data when component mounts
   * Populates all fields with current values from deviceEnumInitData
   * Handles URL-based images by creating special file objects
   */
  useEffect(() => {
    setDeviceName(deviceEnumInitData.name);
    setDeviceType(deviceEnumInitData.type);
    setDeviceSubtitle(deviceEnumInitData.subtitle);
    setDeviceDescription(deviceEnumInitData.description);
    setDeviceOS(deviceEnumInitData.os);
    setSensorList(deviceEnumInitData.sensors);
    setIoList(deviceEnumInitData.io);
    setHardwareList(deviceEnumInitData.hardware);
    setGithubLink(deviceEnumInitData.github);
    setWebLink(deviceEnumInitData.web);
    setIsPrivate(deviceEnumInitData.private);
    setIsAlpha(deviceEnumInitData.alpha);
    setIsBeta(deviceEnumInitData.beta);
    // Set Icon path if url exists
    if (deviceEnumInitData.icon) {
      setIconPath({ url: deviceEnumInitData.icon, isUrl: true });
    }
    // Set Image array path if url exists
    if (deviceEnumInitData.images) {
      const tempArray: any = [];
      for (let i = 0; i < deviceEnumInitData.images.length; i++) {
        tempArray.push({ url: deviceEnumInitData.images[i], isUrl: true });
      }
      setImagePathArray(tempArray);
    }
  }, []);

  /**
   * Handle icon image drop from dropzone
   * @param files - Array of dropped files (max 1 for icon)
   */
  const onIconDrop = async (files: any) => {
    const file = files[0];
    setIconPath(file);
  };

  /**
   * Handle image gallery drop from dropzone
   * Validates max 5 images total
   * @param files - Array of dropped files
   */
  const onImageDropArray = async (files: any) => {
    if (files.length > 5) {
      return;
    }
    if (files.length + imagePathArray.length > 5) {
      return;
    }
    const tempArray = [...imagePathArray];
    files.forEach((file: any) => {
      tempArray.push(file);
    });
    setImagePathArray(tempArray);
  };

  const onDeleteFromImageArray = (index: number) => {
    const tempList = [...imagePathArray];
    tempList.splice(index, 1);
    setImagePathArray(tempList);
  };

  // Reset to initial state from deviceEnumInitData
  const onReset = () => {
    setDeviceName(deviceEnumInitData.name);
    setDeviceType(deviceEnumInitData.type);
    setDeviceSubtitle(deviceEnumInitData.subtitle);
    setDeviceDescription(deviceEnumInitData.description);
    setIconPath({ url: deviceEnumInitData.icon, isUrl: true });
    setDeviceOS(deviceEnumInitData.os);
    setImagePathArray([]);
    deviceEnumInitData.images.forEach((image: string) => {
      setImagePathArray((prevArray) => [
        ...prevArray,
        { url: image, isUrl: true },
      ]);
    });
    setSensorList(deviceEnumInitData.sensors);
    setIoList(deviceEnumInitData.io);
    setHardwareList(deviceEnumInitData.hardware);
    setGithubLink(deviceEnumInitData.github);
    setWebLink(deviceEnumInitData.web);
    setIsPrivate(deviceEnumInitData.private);
    setIsAlpha(deviceEnumInitData.alpha);
    setIsBeta(deviceEnumInitData.beta);
  };

  const resizeFile = (file: any, size: number, type: string) =>
    new Promise((resolve) => {
      Resizer.imageFileResizer(
        file,
        size,
        size,
        type,
        100,
        0,
        (uri) => {
          resolve(uri);
        },
        "blob"
      );
    });

  const checkIfLogoExists = async (path: string) => {
    try {
      const imageRef = ref(firebaseStorage, path);
      await getDownloadURL(imageRef);
      return true;
    } catch (err) {
      return false;
    }
  };

  const uploadImage = async (
    file: any,
    path: string,
    size: number,
    type: string
  ) => {
    const storageRef = ref(firebaseStorage, path);
    try {
      const checkIfExists = await checkIfLogoExists(path);
      if (checkIfExists) {
        await deleteObject(storageRef);
      }
      const resizedFile: any = await resizeFile(file, size, type);
      await uploadBytes(storageRef, resizedFile);
      const downloadUrl = await getDownloadURL(storageRef);
      return downloadUrl;
    } catch (error) {
      console.log(error);
      return "";
    }
  };

  // Upload device enum to firestore collection 'deviceEnums'
  const updateDeviceEnum = async () => {
    setDialogLoading(true);
    try {
      // Compare iconPath to plantEnum.icon
      console.log("Updating icon");
      let tempIconPath;
      if (iconPath) {
        console.log("Iconpath exists");
        if (iconPath.isUrl) {
          console.log("Iconpath is url");
          // If iconPath is a url, set icon to url
          tempIconPath = iconPath.url;
        } else {
          console.log("Iconpath is file");
          // If iconPath is a file, upload file and set icon to url
          const iconPathString = `cloud/deviceEnums/${deviceEnumInitData.type}/${deviceEnumInitData.type}-icon.png`;
          const uploadIcon = await uploadImage(
            iconPath,
            iconPathString,
            500,
            "png"
          );
          tempIconPath = uploadIcon;
        }
      } else {
        console.log("Iconpath does not exist");
        tempIconPath = "";
        // If iconPath is empty, check if plantEnum.icon exists and delete
        if (deviceEnumInitData.icon) {
          const iconRef = ref(
            firebaseStorage,
            `cloud/deviceEnums/${deviceEnumInitData.type}/${deviceEnumInitData.type}-icon.png`
          );
          await deleteObject(iconRef);
        }
      }

      // Compare imagePathArray to plantEnum.images
      console.log("Updating image array");
      let tempImagePathArray = [];
      if (imagePathArray.length > 0) {
        console.log("ImagepathArray exists");
        for (let i = 0; i < imagePathArray.length; i++) {
          console.log("ImagepathArray loop index: ", i);
          if (imagePathArray[i].isUrl) {
            console.log("ImagepathArray index is url");
            // If imagePathArray is a url, compare to plantEnum.images
            if (deviceEnumInitData.images[i] === imagePathArray[i].url) {
              console.log("ImagepathArray index is same as url");
              // If url is the same, set image to url
              tempImagePathArray.push(imagePathArray[i].url);
            } else {
              console.log("ImagepathArray index is different from url");
              // If url is different, fetch image from url and upload file and set image to url
              const fetchImgFromUrl = await fetch(imagePathArray[i].url);
              const blob = await fetchImgFromUrl.blob();
              const newFile = new File(
                [blob],
                `${deviceEnumInitData.type}-${i + 1}.jpeg`,
                { type: "image/jpeg" }
              );
              const imagePathString = `cloud/deviceEnums/${
                deviceEnumInitData.type
              }/${deviceEnumInitData.type}-${i + 1}.jpeg`;
              const uploadImageTemp = await uploadImage(
                newFile,
                imagePathString,
                1000,
                "jpeg"
              );
              tempImagePathArray.push(uploadImageTemp);
            }
          } else {
            console.log("ImagepathArray index is file");
            // If imagePathArray is a file, upload file and set image to url
            const imagePathString = `cloud/deviceEnums/${
              deviceEnumInitData.type
            }/${deviceEnumInitData.type}-${i + 1}.jpeg`;
            const uploadImageTemp = await uploadImage(
              imagePathArray[i],
              imagePathString,
              1000,
              "jpeg"
            );
            tempImagePathArray.push(uploadImageTemp);
          }
        }
      }

      // If imagePathArray is shorter than plantEnum.images, delete the rest
      console.log("Checking if image array is shorter than plantEnum.images");
      if (imagePathArray.length < deviceEnumInitData.images.length) {
        console.log("Image array is shorter than plantEnum.images");
        for (
          let i = imagePathArray.length;
          i < deviceEnumInitData.images.length;
          i++
        ) {
          console.log("Deleting image from plantEnum.images");
          const imageRef = ref(
            firebaseStorage,
            `cloud/deviceEnums/${deviceEnumInitData.type}/${
              deviceEnumInitData.type
            }-${i + 1}.jpeg`
          );
          await deleteObject(imageRef);
        }
      }

      console.log("Updating plant enum");

      const deviceEnumsTemp = {
        name: deviceName,
        type: deviceType,
        subtitle: deviceSubtitle,
        description: deviceDescription,
        icon: tempIconPath,
        os: deviceOS,
        images: tempImagePathArray,
        sensors: sensorList,
        io: ioList,
        hardware: hardwareList,
        github: githubLink,
        web: webLink,
        private: isPrivate,
        alpha: isAlpha,
        beta: isBeta,
      };
      updateDoc(
        doc(firebaseStore, "deviceEnums", deviceEnumInitData.type),
        deviceEnumsTemp
      );

      setSnackbarOpen(true);
      setSnackbarMessage(`${deviceName} updated successfully`);
      setDialogLoading(false);
    } catch (error) {
      console.log(error);
      setDialogLoading(false);
      setSnackbarOpen(true);
      setSnackbarMessage("Error updating device enum");
    }
  };

  return (
    <Dialog
      open={isDialogOpen}
      onClose={handleClose}
      sx={{
        "& .MuiDialog-paper": {
          width: "90vw",
          maxWidth: "1000px",
          display: "flex",
          alignItems: "center",
        },
      }}
    >
      <div className="AddPlantEnumDialog">
        <DialogTitle>Edit device enum</DialogTitle>
        <div className="AddPlantEnumDialogInner">
          <TextField
            sx={primaryColorTextField}
            size="small"
            fullWidth
            label="Device name"
            variant="outlined"
            value={deviceName}
            onChange={(e) => setDeviceName(e.target.value)}
          />
        </div>
        <div className="AddPlantEnumDialogInner">
          <TextField
            sx={primaryColorTextField}
            size="small"
            fullWidth
            label="Type"
            variant="outlined"
            value={deviceType}
            onChange={(e) => setDeviceType(e.target.value)}
          />
        </div>
        <div className="AddPlantEnumDialogInner">
          <TextField
            sx={primaryColorTextField}
            size="small"
            fullWidth
            label="Subtitle"
            variant="outlined"
            value={deviceSubtitle}
            onChange={(e) => setDeviceSubtitle(e.target.value)}
            multiline
            rows={2}
          />
        </div>
        <div className="AddPlantEnumDialogInner">
          <TextField
            sx={primaryColorTextField}
            size="small"
            fullWidth
            label="Description"
            variant="outlined"
            value={deviceDescription}
            onChange={(e) => setDeviceDescription(e.target.value)}
            multiline
            rows={4}
          />
        </div>
        <div className="AddPlantEnumDialogInner">
          <TextField
            sx={primaryColorTextField}
            size="small"
            fullWidth
            label="Github link"
            variant="outlined"
            value={githubLink}
            onChange={(e) => setGithubLink(e.target.value)}
          />
        </div>
        <div className="AddPlantEnumDialogInner">
          <TextField
            sx={primaryColorTextField}
            size="small"
            fullWidth
            label="Web link"
            variant="outlined"
            value={webLink}
            onChange={(e) => setWebLink(e.target.value)}
          />
        </div>
        <div className="AddPlantEnumDialogInner" style={{ marginTop: "40px" }}>
          <p className="AddPlantEnumDialogSwitchText">Is private</p>
          <Switch
            checked={isPrivate}
            onChange={(e) => setIsPrivate(e.target.checked)}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": {
                color: "#44a99a",
              },

              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: "#44a99a",
              },
            }}
          />
        </div>
        <div className="AddPlantEnumDialogInner">
          <p className="AddPlantEnumDialogSwitchText">Is alpha</p>
          <Switch
            checked={isAlpha}
            onChange={(e) => setIsAlpha(e.target.checked)}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": {
                color: "#44a99a",
              },

              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: "#44a99a",
              },
            }}
          />
        </div>
        <div className="AddPlantEnumDialogInner">
          <p className="AddPlantEnumDialogSwitchText">Is beta</p>
          <Switch
            checked={isBeta}
            onChange={(e) => setIsBeta(e.target.checked)}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": {
                color: "#44a99a",
              },

              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: "#44a99a",
              },
            }}
          />
        </div>
        <div className="AddPlantEnumDialogInner" style={{ marginTop: "40px" }}>
          <TextField
            sx={primaryColorTextField}
            size="small"
            fullWidth
            label="OS"
            variant="outlined"
            value={deviceOS}
            onChange={(e) => setDeviceOS(e.target.value)}
          />
        </div>
        <div className="AddPlantEnumDialogOuter">
          <div className="AddPlantEnumDialogInner">
            <TextField
              sx={primaryColorTextField}
              size="small"
              fullWidth
              label="Hardware"
              variant="outlined"
              value={hardwareActual}
              onChange={(e) => setHardwareActual(e.target.value)}
            />
            <IconButton
              sx={{ marginLeft: "10px" }}
              onClick={() => {
                const newList = [...hardwareList];
                newList.push(hardwareActual);
                setHardwareList(newList);
                setHardwareActual("");
              }}
            >
              <AddIcon sx={{ color: "#44a99a" }} />
            </IconButton>
          </div>
          <div className="AddPlantEnumDialogList">
            {hardwareList.map((item, index) => (
              <div key={index} className="AddPlantEnumDialogListItem">
                <p className="AddPlantEnumDialogListItemText">{item}</p>
                <IconButton
                  sx={{ marginLeft: "10px" }}
                  onClick={() => {
                    const newList = [...hardwareList];
                    newList.splice(index, 1);
                    setHardwareList(newList);
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </div>
            ))}
          </div>
        </div>
        <div className="AddPlantEnumDialogOuter" style={{ marginTop: "20px" }}>
          <p className="AddPlantEnumDialogOuterTitle">Sensors</p>
          <div className="AddPlantEnumDialogInner">
            <TextField
              sx={primaryColorTextField}
              size="small"
              fullWidth
              label="Name"
              variant="outlined"
              value={sensorActualName}
              onChange={(e) => setSensorActualName(e.target.value)}
            />
            <IconButton
              sx={{ marginLeft: "10px" }}
              onClick={() => {
                const newList = [...sensorList];
                newList.push({
                  name: sensorActualName,
                  type: sensorActualType,
                  unit: sensorActualUnit,
                  hardware: sensorActualHardware,
                });
                setSensorList(newList);
                setSensorActualHardware("");
                setSensorActualName("");
                setSensorActualType("");
                setSensorActualUnit("");
              }}
            >
              <AddIcon sx={{ color: "#44a99a" }} />
            </IconButton>
          </div>
          <div className="AddPlantEnumDialogInner">
            <TextField
              sx={primaryColorTextField}
              size="small"
              fullWidth
              label="Hardware"
              variant="outlined"
              value={sensorActualHardware}
              onChange={(e) => setSensorActualHardware(e.target.value)}
            />
            <TextField
              sx={{ ...primaryColorTextField, marginLeft: "5px" }}
              size="small"
              fullWidth
              label="Category"
              variant="outlined"
              value={sensorActualType}
              select
            >
              {deviceSensorEnums.map((option) => (
                <MenuItem
                  onClick={() => {
                    setSensorActualType(option.value);
                    setSensorActualUnit(option.unit);
                  }}
                  key={option.value}
                  value={option.value}
                >
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
          </div>
          <div className="AddPlantEnumDialogList">
            {sensorList.map((item, index) => (
              <div key={index} className="AddPlantEnumDialogListItem">
                <p className="AddPlantEnumDialogListItemText">
                  {item.name} - {item.type}
                </p>
                <IconButton
                  sx={{ marginLeft: "10px" }}
                  onClick={() => {
                    const newList = [...sensorList];
                    newList.splice(index, 1);
                    setSensorList(newList);
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </div>
            ))}
          </div>
        </div>
        <div className="AddPlantEnumDialogOuter" style={{ marginTop: "20px" }}>
          <p className="AddPlantEnumDialogOuterTitle">IO</p>
          <div className="AddPlantEnumDialogInner">
            <TextField
              sx={primaryColorTextField}
              size="small"
              fullWidth
              label="Name"
              variant="outlined"
              value={ioActualName}
              onChange={(e) => setIoActualName(e.target.value)}
            />
            <IconButton
              sx={{ marginLeft: "10px" }}
              onClick={() => {
                const newList = [...ioList];
                newList.push({
                  name: ioActualName,
                  type: ioActualType,
                  hardware: ioActualHardware,
                });
                setIoList(newList);
                setIoActualName("");
                setIoActualType("");
                setIoActualHardware("");
              }}
            >
              <AddIcon sx={{ color: "#44a99a" }} />
            </IconButton>
          </div>
          <div className="AddPlantEnumDialogInner">
            <TextField
              sx={primaryColorTextField}
              size="small"
              fullWidth
              label="Hardware"
              variant="outlined"
              value={ioActualHardware}
              onChange={(e) => setIoActualHardware(e.target.value)}
            />
            <TextField
              sx={{ ...primaryColorTextField, marginLeft: "5px" }}
              size="small"
              fullWidth
              label="Category"
              variant="outlined"
              value={ioActualType}
              select
              onChange={(e) => setIoActualType(e.target.value)}
            >
              {deviceIoEnums.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
          </div>
          <div className="AddPlantEnumDialogList">
            {ioList.map((item, index) => (
              <div key={index} className="AddPlantEnumDialogListItem">
                <p className="AddPlantEnumDialogListItemText">
                  {item.name} - {item.type}
                </p>
                <IconButton
                  sx={{ marginLeft: "10px" }}
                  onClick={() => {
                    const newList = [...ioList];
                    newList.splice(index, 1);
                    setIoList(newList);
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </div>
            ))}
          </div>
        </div>
        <div className="AddPlantEnumDialogOuter" style={{ marginTop: "40px" }}>
          <p className="AddPlantEnumDialogOuterTitle">Icon</p>
          <div {...getRootProps({ className: "Dropzone" })}>
            <input {...getInputProps()} />
            <p
              className="AddPlantEnumDialogOuterSub"
              style={{ marginBottom: "10px" }}
            >
              Drag 'n' drop a Icon-image here, or click to select
            </p>
          </div>
          {iconPath && (
            <div className="AddPlantEnumDialogList">
              <p className="AddPlantEnumDialogOuterTitle">
                Chosen icon - Click to delete
              </p>
              {iconPath.isUrl ? (
                <div
                  onClick={() => setIconPath(null)}
                  className="AddPlantEnumDialogListImage"
                >
                  <ImageLoader imgUrl={iconPath.url} radiusStyle={"5px"} />
                </div>
              ) : (
                <div
                  onClick={() => setIconPath(null)}
                  className="AddPlantEnumDialogListImage"
                >
                  <ImageLoader
                    imgUrl={URL.createObjectURL(iconPath)}
                    radiusStyle={"5px"}
                  />
                </div>
              )}
            </div>
          )}
        </div>
        <div className="AddPlantEnumDialogOuter">
          <p className="AddPlantEnumDialogOuterTitle">Images</p>
          <div {...getRootPropsImageArray({ className: "Dropzone" })}>
            <input {...getInputPropsImageArray()} />
            <p className="AddPlantEnumDialogOuterSub">
              Drag 'n' drop up to 5 images here, or click to select
            </p>
          </div>
          {imagePathArray.length > 0 && (
            <p className="AddPlantEnumDialogOuterTitle">
              Chosen images - Click to delete
            </p>
          )}
          {imagePathArray.length > 0 && (
            <div className="AddPlantEnumDialogImageList">
              {imagePathArray.map((item, index) =>
                item.isUrl ? (
                  <div
                    key={index}
                    onClick={() => onDeleteFromImageArray(index)}
                    className="AddPlantEnumDialogListImage"
                  >
                    <ImageLoader imgUrl={item.url} radiusStyle={"5px"} />
                  </div>
                ) : (
                  <div
                    key={index}
                    onClick={() => onDeleteFromImageArray(index)}
                    className="AddPlantEnumDialogListImage"
                  >
                    <ImageLoader
                      imgUrl={URL.createObjectURL(item)}
                      radiusStyle={"5px"}
                    />
                  </div>
                )
              )}
            </div>
          )}
        </div>
        <div
          className="AddPlantEnumDialogInner"
          style={{ marginTop: "60px", justifyContent: "flex-end" }}
        >
          <Button
            disabled={dialogLoading}
            sx={{ marginLeft: "20px", marginRight: "20px" }}
            color="inherit"
            onClick={onReset}
          >
            RESET
          </Button>
          <Button
            disabled={dialogLoading}
            sx={{ marginLeft: "20px", marginRight: "20px" }}
            color="inherit"
            onClick={updateDeviceEnum}
          >
            {dialogLoading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              "UPDATE"
            )}
          </Button>
        </div>
      </div>
      <Snackbar
        open={snackbarOpen}
        message={snackbarMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
      />
    </Dialog>
  );
}

export default EditDeviceEnum;
